import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Container, LinearProgress, Stack } from "@mui/material";
import { useLocation } from "react-router-dom";

import { describePlan, hasActiveSubscriptionAccess, isInGracePeriod, SUBSCRIPTION_GRACE_DAYS } from "../../subscription";
import { useAuth } from "../auth/AuthContext";
import AccountImportPanel from "./AccountImportPanel";
import AccountHeader from "./account/AccountHeader";
import AccountSnackbar from "./account/AccountSnackbar";
import CancelSubscriptionDialog from "./account/CancelSubscriptionDialog";
import PasswordCard from "./account/PasswordCard";
import PlanChangeDialog from "./account/PlanChangeDialog";
import SubscriptionCard from "./account/SubscriptionCard";
import { formatIsoDate } from "./account/formatters";
import useAccountData from "./account/useAccountData";
import usePlanOptions from "./account/usePlanOptions";

export default function Account() {
    const location = useLocation();
    const { updateUser } = useAuth();

    const { userInfo, loading, loadError, reload, refreshUser } = useAccountData({ updateUser });

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [planDialogOpen, setPlanDialogOpen] = useState(false);

    const { planOptions, planOptionsLoading, planOptionsError } = usePlanOptions({ open: planDialogOpen });

    const handleNotify = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const planDescription = useMemo(() => {
        if (!userInfo?.paymentPlan) return "–";
        return describePlan(userInfo.paymentPlan);
    }, [userInfo?.paymentPlan]);

    const paidUntilDisplay = useMemo(() => formatIsoDate(userInfo?.paidUntil), [userInfo?.paidUntil]);

    const cancellationDateDisplay = useMemo(
        () => formatIsoDate(userInfo?.cancellationEffectiveDate),
        [userInfo?.cancellationEffectiveDate]
    );

    const cancellationRequested = Boolean(userInfo?.cancellationRequested);
    const checkoutRequired = new URLSearchParams(location.search).get("checkout") === "required";
    const graceActive = Boolean(userInfo && isInGracePeriod(userInfo));

    useEffect(() => {
        if (checkoutRequired && userInfo && !hasActiveSubscriptionAccess(userInfo)) {
            setPlanDialogOpen(true);
        }
    }, [checkoutRequired, userInfo]);

    return (
        <>
            <AccountHeader onReload={reload} />

            <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
                {loading && (
                    <Box sx={{ my: 3 }}>
                        <LinearProgress />
                    </Box>
                )}
                {loadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {loadError}
                    </Alert>
                )}

                {!loading && !loadError && (
                    <Stack spacing={3}>
                        {checkoutRequired && !hasActiveSubscriptionAccess(userInfo) && (
                            <Alert severity="info">
                                Dein Konto ist angelegt, aber noch nicht freigeschaltet. Bitte wähle jetzt einen Tarif
                                und schließe die Zahlung ab.
                            </Alert>
                        )}
                        {graceActive && (
                            <Alert severity="warning">
                                Deine Zahlung ist überfällig (bezahlt bis {paidUntilDisplay}). Dein Zugang bleibt noch
                                im Rahmen der Karenzzeit von {SUBSCRIPTION_GRACE_DAYS} Tagen aktiv. Bitte verlängere
                                jetzt dein Abonnement, um eine Sperrung zu vermeiden.
                            </Alert>
                        )}
                        <SubscriptionCard
                            userInfo={userInfo}
                            planDescription={planDescription}
                            paidUntilDisplay={paidUntilDisplay}
                            cancellationRequested={cancellationRequested}
                            cancellationDateDisplay={cancellationDateDisplay}
                            onOpenPlanDialog={() => setPlanDialogOpen(true)}
                            onOpenCancelDialog={() => setCancelDialogOpen(true)}
                        />

                        <AccountImportPanel />

                        <PasswordCard onNotify={handleNotify} />
                    </Stack>
                )}

                <CancelSubscriptionDialog
                    open={cancelDialogOpen}
                    onClose={() => setCancelDialogOpen(false)}
                    paidUntilDisplay={paidUntilDisplay}
                    initialCancelDate={userInfo?.paidUntil ?? ""}
                    onRefreshUser={refreshUser}
                    onNotify={handleNotify}
                />

                <PlanChangeDialog
                    open={planDialogOpen}
                    onClose={() => setPlanDialogOpen(false)}
                    planOptions={planOptions}
                    planOptionsLoading={planOptionsLoading}
                    planOptionsError={planOptionsError}
                    onRefreshUser={refreshUser}
                    onNotify={handleNotify}
                />

                <AccountSnackbar
                    open={snackbarOpen}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={() => setSnackbarOpen(false)}
                />
            </Container>
        </>
    );
}
